import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="code-input"
//
// Renders a row of single-character boxes that together capture a numeric code (e.g. the
// 6-digit sign-up code). Auto-advances on type, steps back on backspace, supports arrow
// keys, and distributes a pasted code across the boxes. The combined value is mirrored into
// a hidden field (the "hidden" target) so the form submits a single value.
export default class extends Controller {
  static targets = ["box", "hidden"]

  connect() {
    this.sync()
    // Auto-focus the first box on load — but only if it's visible (not in a hidden tab,
    // e.g. when "No sign-up code" is the active activation method).
    const first = this.boxTargets[0]
    if (first && first.offsetParent !== null) first.focus()
  }

  onInput(event) {
    const box = event.target
    // Keep digits only, and only the most recently typed one.
    box.value = box.value.replace(/\D/g, "").slice(-1)
    if (box.value) {
      const next = this.boxTargets[this.indexOf(box) + 1]
      if (next) next.focus()
    }
    this.sync()
  }

  onKeydown(event) {
    const box = event.target
    const i = this.indexOf(box)
    if (event.key === "Backspace" && !box.value) {
      // Current box already empty: delete the previous digit and move back in one press.
      const prev = this.boxTargets[i - 1]
      if (prev) {
        prev.value = ""
        prev.focus()
        this.sync()
        event.preventDefault()
      }
    } else if (event.key === "ArrowLeft") {
      this.boxTargets[i - 1]?.focus()
    } else if (event.key === "ArrowRight") {
      this.boxTargets[i + 1]?.focus()
    }
  }

  onPaste(event) {
    event.preventDefault()
    const digits = (event.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, this.boxTargets.length)
    if (!digits) return
    this.boxTargets.forEach((box, i) => { box.value = digits[i] || "" })
    const focusIndex = Math.min(digits.length, this.boxTargets.length - 1)
    this.boxTargets[focusIndex]?.focus()
    this.sync()
  }

  sync() {
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = this.boxTargets.map((b) => b.value).join("")
    }
  }

  indexOf(box) {
    return this.boxTargets.indexOf(box)
  }
}
